import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import LogoWordmark from '../../assets/logo-wordmark.svg';

// Background photography via picsum.photos (seeded — stable, deterministic
// URLs, no API key). A dark-to-accent gradient is layered on top so any
// underlying photo reads as "modern fintech" regardless of subject; swap
// these src values for real brand photography whenever it's available.
const SLIDES = [
  {
    seed: 'canvainvest-skyline',
    kicker: 'Built for growth',
    title: 'Grow your wealth with confidence',
    body: 'Track fixed-return investments in real time, with the same rigor a real fintech product demands.',
  },
  {
    seed: 'canvainvest-team',
    kicker: 'Bank-grade security',
    title: 'KYC-verified, every step of the way',
    body: 'Your identity and funds are protected by verification workflows built for regulated finance.',
  },
  {
    seed: 'canvainvest-charts',
    kicker: 'Full transparency',
    title: 'Every naira, accounted for',
    body: 'Portfolio value, ROI, and payout history — always a click away, never a mystery.',
  },
  {
    seed: 'canvainvest-city',
    kicker: 'Trusted nationwide',
    title: 'Trusted by investors across Nigeria',
    body: 'From Lagos to Kaduna, investors rely on Cavan Capital to track their capital with confidence.',
  },
];

const AUTO_ADVANCE_MS = 5500;

export default function AuthSlider() {
  const [active, setActive] = useState(0);
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const timerRef = useRef(null);

  const goTo = (index) => setActive((index + SLIDES.length) % SLIDES.length);

  useEffect(() => {
    timerRef.current = window.setInterval(() => goTo(active + 1), AUTO_ADVANCE_MS);
    return () => window.clearInterval(timerRef.current);
  }, [active]);

  useEffect(() => {
    if (!textRef.current) return;
    gsap.fromTo(
      textRef.current,
      { opacity: 0, y: 16 },
      { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }
    );
  }, [active]);

  return (
    <div
      ref={containerRef}
      className="relative hidden lg:flex lg:w-[46%] xl:w-1/2 min-h-screen overflow-hidden bg-background-surface"
      onMouseEnter={() => window.clearInterval(timerRef.current)}
      onMouseLeave={() => {
        timerRef.current = window.setInterval(() => goTo(active + 1), AUTO_ADVANCE_MS);
      }}
    >
      {SLIDES.map((slide, i) => (
        <div
          key={slide.seed}
          className="absolute inset-0 transition-opacity duration-[1200ms] ease-out"
          style={{ opacity: i === active ? 1 : 0, zIndex: i === active ? 1 : 0 }}
          aria-hidden={i !== active}
        >
          <img
            src={`https://picsum.photos/seed/${slide.seed}/1200/1600`}
            alt=""
            className="h-full w-full object-cover scale-105"
            loading={i === 0 ? 'eager' : 'lazy'}
          />
          {/* Uniform dark wash so text/logo stay legible over any photo,
              plus extra bottom-weighted darkening under the caption. */}
          <div className="absolute inset-0 bg-background/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/30 to-background/10" />
          <div className="absolute inset-0 bg-gradient-to-tr from-accent/15 via-transparent to-transparent" />
        </div>
      ))}

      <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
        <img src={LogoWordmark} alt="Cavan Capital" className="h-8 w-auto" />

        <div ref={textRef} key={active} className="max-w-md">
          <p className="text-accent text-sm font-semibold tracking-wide uppercase mb-3">{SLIDES[active].kicker}</p>
          <h2 className="text-3xl xl:text-4xl font-semibold text-foreground text-balance leading-tight mb-4">
            {SLIDES[active].title}
          </h2>
          <p className="text-foreground-muted text-base leading-relaxed">{SLIDES[active].body}</p>
        </div>

        <div className="flex gap-2">
          {SLIDES.map((slide, i) => (
            <button
              key={slide.seed}
              onClick={() => goTo(i)}
              aria-label={`Show slide ${i + 1}: ${slide.title}`}
              aria-current={i === active}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === active ? 'w-8 bg-accent' : 'w-4 bg-foreground/25 hover:bg-foreground/40'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
